/* eslint-disable @typescript-eslint/no-explicit-any */
import { API_URL } from '@/configs/api.config';
import authLocalStorage from '@/features/auth/_shared/utils/auth-localstorage';
import { captureSentryError } from '@/features/sentry/utils/sentry';
import axios, { InternalAxiosRequestConfig, AxiosError } from 'axios';

/**
 * Safely parse request payload for Sentry reporting
 */
const safeParsePayload = (data: any) => {
  try {
    // If data is already an object, return it
    if (typeof data === 'object' && data !== null) {
      return data;
    }
    // If data is a string, try to parse it
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
    return data;
  } catch {
    // If parsing fails, return the original data as string
    return data?.toString() || null;
  }
};

/**
 * Create a custom error with meaningful name for Sentry
 */
const createCustomApiError = (error: AxiosErrorCustom, customName: string) => {
  const customError = new Error(axiosExtractErrorMessage(error));
  customError.name = customName;
  customError.stack = error.stack;
  return customError;
};

/**
 * Api response type
 */
export interface ApiResponse<T> {
  headers: any;
  message?: string;
  statusCode: number;
  method: string;
  path: string;
  payload: T;
}

/**
 * Error response type similar to ApiResponse but with required message
 */
export interface ApiErrorResponse extends Error {
  message: string;
  statusCode?: number;
  method?: string;
  path?: string;
  payload?: {
    message?: string;
  }; // Optional payload (different from ApiResponse)
}

/**
 * Custom Axios error type with typed response data
 */
export type AxiosErrorCustom = AxiosError<ApiErrorResponse>;

/**
 * Extract error message from Axios error
 */
export const axiosExtractErrorMessage = (error: AxiosErrorCustom, defaultMessage?: string) => {
  return error?.response?.data?.payload?.message || defaultMessage || 'Something went wrong!';
};

/**
 * Axios instance for public requests
 */
export const axiosInstancePublic = axios.create({
  baseURL: API_URL,
  timeout: 1000 * 60, // 60 seconds
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

axiosInstancePublic.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error: AxiosErrorCustom) => {
    // Create custom error name based on status and endpoint
    const endpoint = error.config?.url;
    const status = error.response?.status || 'network';
    const customErrorName = `APIError_${status}_${endpoint}`;

    // Capture public API errors in Sentry with custom name
    const customError = createCustomApiError(error, customErrorName);
    captureSentryError(customError, {
      api_type: 'public',
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      error_message: axiosExtractErrorMessage(error),
      request_payload: safeParsePayload(error.config?.data),
      request_params: error.config?.params || null,
    });

    return Promise.reject(error);
  }
);

/**
 * Axios instance for authenticated requests
 */

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  const token = authLocalStorage.getToken();
  if (token) {
    config.headers!.authorization = `Bearer ${token}`;
  }
  return config;
}

export const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 1000 * 60, // 60 seconds
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

axiosInstance.interceptors.request.use(authRequestInterceptor);
axiosInstance.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error: AxiosErrorCustom) => {
    const _message = error.response?.data?.message || error.message;
    // console.log(message);

    const path = window.location.pathname;

    // Capture authenticated API errors in Sentry (except 401 auth errors)
    if (error?.response?.status !== 401) {
      // Create custom error name based on status and endpoint
      const endpoint = error.config?.url;
      const status = error.response?.status || 'network';
      const customErrorName = `APIError_${status}_${endpoint}`;

      const customError = createCustomApiError(error, customErrorName);
      captureSentryError(customError, {
        api_type: 'authenticated',
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status,
        error_message: axiosExtractErrorMessage(error),
        user_token: !!authLocalStorage.getToken(),
        current_path: path,
        request_payload: safeParsePayload(error.config?.data),
        request_params: error.config?.params || null,
      });
    }

    // redirect back to root route when user is not logged in
    if (
      error?.response?.status === 401 &&
      !(path.includes('login') || path.includes('signup') || path.includes('password'))
    ) {
      // Capture authentication error with custom name
      const endpoint = error.config?.url;
      const customErrorName = `APIError_401_${endpoint}`;

      const customError = createCustomApiError(error, customErrorName);
      captureSentryError(customError, {
        api_type: 'authentication',
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: 401,
        error_message: 'User authentication failed - redirecting to login',
        current_path: path,
        request_payload: safeParsePayload(error.config?.data),
        request_params: error.config?.params || null,
      });

      authLocalStorage.clearToken();
      authLocalStorage.clearUser();

      window.location.assign('/');
    }
    return Promise.reject(error);
  }
);
